import React, { useState, useEffect, useMemo, useRef } from 'react';

const API = 'https://talku.online';
const SF = "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
// Paleta sólida: negro, blanco y verde Talku (sin pasteles)
const INK = '#0a0a0a';
const GREEN = '#a3e31c';

const fmtMoney = (v) => (v || v === 0) ? `$${Number(v).toLocaleString('es-CO')}` : '—';
const fmtInt = (v) => (v || v === 0) ? Number(v).toLocaleString('es-CO') : '—';
const fmtDate = (iso) => {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  if (isNaN(d)) return 'Sin fecha';
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }).replace('.', '');
};

// ── prefers-reduced-motion ──
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return reduced;
}

// ── count-up numérico suave (tween desde valor previo → nuevo) ──
function useCountUp(target, reduced) {
  const [val, setVal] = useState(target || 0);
  const prev = useRef(target || 0);
  useEffect(() => {
    const to = Number(target) || 0;
    if (reduced) { setVal(to); prev.current = to; return; }
    const from = prev.current; const dur = 500; const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setVal(from + (to - from) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick); else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, reduced]);
  return val;
}

function BigNumber({ value, money, loading, reduced }) {
  const n = useCountUp(loading ? 0 : value, reduced);
  if (loading) return <span className="db-skel db-skel-num" />;
  if (typeof value !== 'number') return '—';
  return money ? fmtMoney(Math.round(n)) : fmtInt(Math.round(n));
}

const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>
);

// ── Barras semanales: negro sólido = esta semana, rayado = semana anterior ──
function WeekBars({ cur, prev }) {
  const max = Math.max(...cur.map(d => d.v), ...prev.map(d => d.v), 1);
  const ticks = [max, Math.round(max / 2), 0];
  return (
    <div className="db-wb">
      <div className="db-wb-y">{ticks.map((t, i) => <span key={i}>{fmtInt(t)}</span>)}</div>
      <div className="db-wb-cols">
        {cur.map((d, i) => (
          <div className="db-wb-col" key={i} title={`${d.label}: ${fmtInt(d.v)} esta semana · ${fmtInt(prev[i]?.v || 0)} semana anterior`}>
            <div className="db-wb-pair">
              <span className="db-wb-bar hatch" style={{ height: `${((prev[i]?.v || 0) / max) * 100}%` }} />
              <span className="db-wb-bar solid" style={{ height: `${(d.v / max) * 100}%` }} />
            </div>
            <span className="db-wb-lbl">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Medidor semicircular de marcas (como el de la referencia) ──
function Gauge({ pct }) {
  const N = 40, R = 92, r = 70, cx = 110, cy = 104;
  const on = typeof pct === 'number' ? Math.round((pct / 100) * N) : 0;
  return (
    <svg viewBox="0 0 220 112" className="db-gauge">
      {Array.from({ length: N + 1 }).map((_, i) => {
        const a = Math.PI - (i / N) * Math.PI;
        return (
          <line key={i} x1={cx + r * Math.cos(a)} y1={cy - r * Math.sin(a)} x2={cx + R * Math.cos(a)} y2={cy - R * Math.sin(a)}
            stroke={INK} strokeOpacity={i <= on ? 1 : 0.18} strokeWidth="2.4" strokeLinecap="round" />
        );
      })}
    </svg>
  );
}

// ── Gráfico de líneas (SVG): mensajes recibidos (negro) + conversaciones nuevas (verde) ──
function LineChart({ data }) {
  const [hi, setHi] = useState(null);
  const n = data.length;
  if (n === 0) return <div className="db-empty">Sin datos en el rango seleccionado.</div>;
  const W = 640, H = 210, PX = 6, PY = 14;
  const maxC = Math.max(...data.map(d => d.count), 1);
  const maxR = Math.max(...data.map(d => d.revenue), 1);
  const xAt = i => (n === 1 ? W / 2 : PX + (i * (W - 2 * PX)) / (n - 1));
  const yC = v => H - PY - (v / maxC) * (H - 2 * PY);
  const yR = v => H - PY - (v / maxR) * (H - 2 * PY);
  const smooth = (yf) => {
    const pts = data.map((d, i) => [xAt(i), yf(d)]);
    if (pts.length === 1) return `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    let dp = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    const t = 0.18;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      const c1x = p1[0] + (p2[0] - p0[0]) * t, c1y = p1[1] + (p2[1] - p0[1]) * t;
      const c2x = p2[0] - (p3[0] - p1[0]) * t, c2y = p2[1] - (p3[1] - p1[1]) * t;
      dp += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
    }
    return dp;
  };
  const pathR = smooth(d => yR(d.revenue));
  const pathC = smooth(d => yC(d.count));
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const rel = ((e.clientX - r.left) / r.width) * W;
    const idx = Math.round((rel - PX) / ((W - 2 * PX) / Math.max(1, n - 1)));
    setHi(Math.max(0, Math.min(n - 1, idx)));
  };
  const step = Math.max(1, Math.ceil(n / 8));
  return (
    <div className="db-line-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="db-line-svg" onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
        {[0.25, 0.5, 0.75].map((g, i) => <line key={i} x1="0" x2={W} y1={H * g} y2={H * g} className="db-line-grid" />)}
        <path d={pathR} className="db-line ink" />
        <path d={pathC} className="db-line green" />
        {hi != null && <line x1={xAt(hi)} x2={xAt(hi)} y1="0" y2={H} className="db-line-cursor" />}
        {hi != null && <circle cx={xAt(hi)} cy={yR(data[hi].revenue)} r="4" fill={INK} />}
        {hi != null && <circle cx={xAt(hi)} cy={yC(data[hi].count)} r="4" fill={GREEN} stroke={INK} strokeWidth="1.5" />}
      </svg>
      {hi != null && (
        <div className="db-tip" style={{ left: `${Math.max(10, Math.min(90, (xAt(hi) / W) * 100))}%` }}>
          <div className="db-tip-date">{data[hi].label}</div>
          <div>Mensajes recibidos: <b>{fmtInt(data[hi].revenue)}</b></div>
          <div>Conversaciones nuevas: <b>{fmtInt(data[hi].count)}</b></div>
          {data[hi].sales > 0 && <div>Ventas del bot: <b>{fmtInt(data[hi].sales)} · {fmtMoney(data[hi].salesRev)}</b></div>}
        </div>
      )}
      <div className="db-xlabels">
        {data.map((d, i) => <span key={i} style={{ opacity: (n <= 12 || i % step === 0) ? 1 : 0 }}>{d.label}</span>)}
      </div>
      <div className="db-legend">
        <span><span className="db-lg-dot ink" />Mensajes recibidos</span>
        <span><span className="db-lg-dot green" />Conversaciones nuevas</span>
      </div>
    </div>
  );
}

const BOARD_COLS = [
  { key: 'nuevas', title: 'Nuevas', note: 'últimas 24 h' },
  { key: 'bot', title: 'Atendidas por el bot', note: 'activas 24 h' },
  { key: 'escaladas', title: 'Escaladas a asesor', note: 'pendientes' },
  { key: 'venta', title: 'Venta cerrada', note: 'por el bot · 30 días' },
];

export default function Dashboard({ user, onNav }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState('Semana');     // Día | Semana | 30 días | Personalizado
  const [range, setRange] = useState(() => {
    const iso = d => d.toISOString().slice(0, 10);
    const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 29);
    return { from: iso(from), to: iso(to) };
  });
  const [series, setSeries] = useState([]);
  // Deltas en vivo (SSE); el polling de 60 s los reconcilia a 0
  const [live, setLive] = useState({ dMsgs: 0, dConvs: 0, dEsc: 0, dFail: 0 });
  const reduced = usePrefersReducedMotion();

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Buenos días' : now.getHours() < 18 ? 'Buenas tardes' : 'Buenas noches';
  const userName = user?.name || user?.email?.split('@')[0] || '';
  const token = () => user?.token || localStorage.getItem('talku_token') || '';

  const load = async () => {
    try {
      const res = await fetch(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error('bad');
      setStats(await res.json());
      setError(false);
      setLive({ dMsgs: 0, dConvs: 0, dEsc: 0, dFail: 0 });
    } catch {
      setError(true);
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [user]); // eslint-disable-line

  // ── SSE: tiempo real (aditivo; el polling sigue como respaldo) ──
  useEffect(() => {
    const tk = token();
    if (!tk || typeof EventSource === 'undefined') return;
    let es = null, closed = false, backoff = 1000, timer = null;
    const onEvent = (ev) => setLive(l => {
      switch (ev.type) {
        case 'message.inbound': return { ...l, dMsgs: l.dMsgs + 1 };
        case 'conversation.new': return { ...l, dConvs: l.dConvs + 1 };
        case 'conversation.escalated': return { ...l, dEsc: l.dEsc + 1 };
        case 'message.failed': return { ...l, dFail: l.dFail + 1 };
        default: return l;
      }
    });
    const retry = () => {
      if (!closed) { timer = setTimeout(connect, Math.min(backoff, 30000)); backoff = Math.min(backoff * 2, 30000); }
    };
    const connect = async () => {
      if (closed) return;
      let ticket = '';
      try {
        const r = await fetch(`${API}/dashboard/stream-ticket`, { method: 'POST', headers: { Authorization: `Bearer ${tk}` } });
        if (r.ok) ticket = (await r.json()).ticket || '';
      } catch { /* ignore */ }
      if (!ticket || closed) { retry(); return; }
      es = new EventSource(`${API}/dashboard/stream?ticket=${encodeURIComponent(ticket)}`);
      es.onopen = () => { backoff = 1000; };
      es.onmessage = (e) => { try { onEvent(JSON.parse(e.data)); } catch { /* ignore */ } };
      es.onerror = () => { if (es) es.close(); retry(); };
    };
    connect();
    return () => { closed = true; if (timer) clearTimeout(timer); if (es) es.close(); };
  }, [user]); // eslint-disable-line

  // ── Serie por rango (solo Personalizado) ──
  useEffect(() => {
    if (period !== 'Personalizado' || !range.from || !range.to) return;
    let cancel = false;
    fetch(`${API}/dashboard/series?frm=${range.from}&to=${range.to}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : { series: [] }))
      .then(d => { if (!cancel) setSeries(d.series || []); })
      .catch(() => { if (!cancel) setSeries([]); });
    return () => { cancel = true; };
  }, [period, range.from, range.to, user]); // eslint-disable-line

  const s = stats || {};
  const k = s.kpis || {};
  const bot = s.bot || {};
  const al = s.alerts || {};
  const ct = s.contacts || {};
  const sr = s.series || {};
  const board = s.board || {};
  const topProducts = s.top_bot_products || [];

  // ── Evolución según periodo ──
  const chartData = useMemo(() => {
    if (period === 'Personalizado') {
      return (series || []).map(p => ({ label: (p.date || '').slice(5), revenue: p.messages || 0, count: p.conversations || 0, sales: p.sales || 0, salesRev: p.revenue || 0 }));
    }
    const src = period === 'Día' ? (sr.day || []) : period === '30 días' ? (sr.month || []) : (sr.week || []);
    return src.map(b => ({ label: b.label, revenue: b.messages || 0, count: b.conversations || 0, sales: b.sales || 0, salesRev: b.revenue || 0 }));
  }, [period, series, sr]);
  const sum = (key) => chartData.reduce((a, b) => a + (b[key] || 0), 0);

  // ── Hero: esta semana vs semana anterior (de la serie de 30 días) ──
  const month = sr.month || [];
  const weekCur = (sr.week || []).map((d, i) => ({ label: d.label, v: (d.messages || 0) + (i === 6 ? live.dMsgs : 0) }));
  const weekPrev = month.slice(-14, -7).map(d => ({ v: d.messages || 0 }));

  const mkTrend = (a, b) => {
    if (typeof a !== 'number' || typeof b !== 'number' || b <= 0) return null;
    const d = Math.round(((a - b) / b) * 100);
    return `${d > 0 ? '+' : ''}${d}% vs ayer`;
  };
  const revTrend = mkTrend(k.bot_revenue_today, k.bot_revenue_yesterday);

  const alerts = [
    { key: 'esc', label: 'Escalados', value: typeof al.escalated === 'number' ? al.escalated + live.dEsc : al.escalated },
    { key: 'unread', label: 'Sin leer', value: al.unread },
    { key: 'fail', label: 'Envíos fallidos hoy', value: typeof al.failed_sends_today === 'number' ? al.failed_sends_today + live.dFail : al.failed_sends_today },
  ];
  const botRows = [
    { name: 'Respuestas del bot', value: bot.replies_bot_30d, tone: 'ink' },
    { name: 'Respuestas de asesores', value: bot.replies_human_30d, tone: 'green' },
    { name: 'Transferencias a asesor', value: bot.transfers_30d, tone: 'ink' },
    { name: 'Ventas del bot', value: bot.sales_30d, tone: 'green' },
  ];
  const botMax = Math.max(...botRows.map(r => (typeof r.value === 'number' ? r.value : 0)), 1);
  const openConv = (phone) => onNav && onNav('Conversaciones', phone || undefined);

  return (
    <div className="db-root">
      <style>{`
        .db-root { flex:1; overflow-y:auto; background:#fff; font-family:${SF}; padding:32px 40px 48px; color:${INK}; }
        .db-inner { max-width:1400px; margin:0 auto; }
        .db-root::-webkit-scrollbar{width:6px;} .db-root::-webkit-scrollbar-thumb{background:#d4d4d4;border-radius:3px;}
        @keyframes dbIn { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:none;} }
        .db-anim { animation:dbIn .35s cubic-bezier(.2,.7,.2,1) both; }

        /* header */
        .db-header { margin-bottom:20px; }
        .db-h-greet { font-size:28px; font-weight:700; letter-spacing:-.6px; margin:0; }
        .db-h-sub { font-size:13px; color:#6B7280; margin:6px 0 0; }
        .db-tabs { display:inline-flex; gap:2px; flex-wrap:wrap; }
        .db-tab { border:none; background:transparent; font-family:${SF}; font-size:13px; font-weight:400; color:#6B7280; padding:7px 13px; border-radius:6px; cursor:pointer; transition:background .15s,color .15s; }
        .db-tab:hover { color:${INK}; }
        .db-tab.active { background:${INK}; color:#fff; }
        .db-range { display:inline-flex; align-items:center; gap:6px; margin-left:6px; }
        .db-date { border:none; background:#F4F4F4; border-radius:6px; padding:7px 12px; font-family:${SF}; font-size:12.5px; color:${INK}; outline:none; }

        .db-err { display:flex; align-items:center; justify-content:space-between; gap:12px; background:${INK}; color:#fff; border-radius:8px; padding:12px 16px; font-size:13px; margin-bottom:16px; }
        .db-btn { border:none; background:#fff; color:${INK}; font-family:${SF}; font-weight:400; font-size:12.5px; padding:8px 16px; border-radius:6px; cursor:pointer; }

        /* hero */
        .db-hero { background:#f6f6f6; border:1px solid #e6e6e6; border-radius:10px; padding:28px 32px; display:grid; grid-template-columns:1.25fr 1fr .7fr .7fr; gap:28px; align-items:center; margin-bottom:28px; }
        .db-hero-t { font-size:14px; font-weight:700; margin:0 0 12px; }
        .db-wb { display:flex; gap:10px; height:150px; }
        .db-wb-y { display:flex; flex-direction:column; justify-content:space-between; font-size:10.5px; color:rgba(10,10,10,.6); padding-bottom:20px; text-align:right; min-width:22px; }
        .db-wb-cols { flex:1; display:flex; gap:10px; align-items:stretch; }
        .db-wb-col { flex:1; display:flex; flex-direction:column; align-items:center; gap:6px; min-width:0; }
        .db-wb-pair { flex:1; width:100%; display:flex; align-items:flex-end; justify-content:center; gap:3px; }
        .db-wb-bar { width:42%; max-width:16px; border-radius:2px 2px 0 0; transition:height .5s cubic-bezier(.2,.7,.2,1); min-height:2px; }
        .db-wb-bar.solid { background:${INK}; }
        .db-wb-bar.hatch { background:repeating-linear-gradient(135deg, ${INK} 0 2px, transparent 2px 6px); box-shadow:inset 0 0 0 1.5px ${INK}; }
        .db-wb-lbl { font-size:11px; font-weight:600; color:rgba(10,10,10,.7); }
        .db-hero-legend { display:flex; gap:14px; font-size:11px; margin-top:8px; color:rgba(10,10,10,.75); }
        .db-hero-legend i { display:inline-block; width:10px; height:10px; border-radius:2px; margin-right:5px; vertical-align:-1px; }
        .db-gauge-wrap { position:relative; text-align:center; }
        .db-gauge { width:100%; max-width:260px; display:block; margin:0 auto; }
        .db-gauge-val { position:absolute; left:0; right:0; bottom:4px; }
        .db-gauge-val b { display:block; font-size:34px; font-weight:700; letter-spacing:-1px; line-height:1; }
        .db-gauge-val span { font-size:12px; color:rgba(10,10,10,.7); }
        .db-big { display:flex; flex-direction:column; gap:22px; }
        .db-big-n { font-size:34px; font-weight:700; letter-spacing:-1px; line-height:1; }
        .db-big-row { display:flex; align-items:flex-end; justify-content:space-between; gap:8px; }
        .db-big-l { font-size:12px; line-height:1.35; color:rgba(10,10,10,.75); }
        .db-big-btn { border:none; background:transparent; color:${INK}; cursor:pointer; padding:4px; border-radius:6px; display:inline-flex; transition:background .15s, transform .15s; }
        .db-big-btn:hover { background:rgba(10,10,10,.1); transform:translateX(2px); }
        .db-big-trend { font-size:11.5px; font-weight:600; }

        /* tablero */
        .db-board { display:grid; grid-template-columns:repeat(4, minmax(0,1fr)); gap:18px; margin-bottom:28px; }
        .db-col-h { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .db-col-t { font-size:17px; font-weight:700; letter-spacing:-.3px; }
        .db-col-note { font-size:11.5px; color:#6B7280; margin-top:2px; }
        .db-col-n { font-size:12px; font-weight:400; background:#F4F4F4; border-radius:4px; padding:4px 10px; }
        .db-col-list { display:flex; flex-direction:column; gap:10px; }
        .db-tk { background:#fff; border:1px solid #E8E8E8; border-radius:8px; padding:14px; cursor:pointer; text-align:left; font-family:${SF}; color:${INK}; transition:border-color .15s, transform .15s, box-shadow .15s; width:100%; }
        .db-tk:hover { border-color:#bdbdbd; transform:translateY(-1px); box-shadow:0 4px 14px rgba(10,10,10,.06); }
        .db-tk.dark { background:${INK}; color:#fff; border-color:${INK}; }
        .db-tk-name { font-size:14px; font-weight:700; margin-bottom:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .db-tk-prev { font-size:12px; color:#6B7280; line-height:1.4; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; min-height:34px; }
        .db-tk.dark .db-tk-prev { color:rgba(255,255,255,.7); }
        .db-tk-foot { display:flex; align-items:center; justify-content:space-between; margin-top:12px; }
        .db-chip { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:400; border:1px solid #E8E8E8; border-radius:4px; padding:3px 8px; }
        .db-tk.dark .db-chip { border-color:rgba(255,255,255,.25); }
        .db-unread { background:${GREEN}; color:${INK}; border-radius:4px; padding:1px 8px; font-size:11.5px; font-weight:400; }
        .db-col-empty { font-size:12px; color:#9CA3AF; border:1px dashed #E8E8E8; border-radius:8px; padding:18px 14px; text-align:center; }

        /* tarjetas */
        .db-grid { display:grid; grid-template-columns:repeat(12, minmax(0,1fr)); gap:18px; margin-bottom:18px; }
        .col8{grid-column:span 8;} .col4{grid-column:span 4;}
        .db-card { background:#fff; border:1px solid #E8E8E8; border-radius:8px; padding:22px; min-width:0; }
        .db-ct { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:16px; flex-wrap:wrap; }
        .db-ct h3 { font-size:16px; font-weight:700; margin:0; letter-spacing:-.2px; }
        .db-ct-note { font-size:11.5px; color:#6B7280; }
        .db-row { display:flex; align-items:center; gap:10px; padding:9px 0; }
        .db-row + .db-row { border-top:1px solid #F2F2F2; }
        .db-row-name { font-size:13px; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .db-track { flex:0 0 90px; height:6px; background:#F2F2F2; border-radius:2px; overflow:hidden; }
        .db-fill { display:block; height:100%; border-radius:2px; transition:width .6s cubic-bezier(.2,.7,.2,1); }
        .db-fill.ink { background:${INK}; } .db-fill.green { background:${GREEN}; }
        .db-row-val { font-size:14px; font-weight:700; min-width:44px; text-align:right; }
        .db-foot { display:flex; gap:28px; margin-top:12px; padding-top:14px; border-top:1px solid #F2F2F2; flex-wrap:wrap; }
        .db-foot-lbl { font-size:11px; color:#6B7280; } .db-foot-val { font-size:18px; font-weight:700; margin-top:2px; }
        .db-alert { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; border-radius:6px; background:#F7F7F7; margin-bottom:8px; border:none; width:100%; font-family:${SF}; cursor:pointer; color:${INK}; transition:background .15s; }
        .db-alert:hover { background:#EFEFEF; }
        .db-alert.on { background:${INK}; color:#fff; }
        .db-alert.on:hover { background:#222; }
        .db-alert-lbl { font-size:13px; font-weight:400; }
        .db-alert-n { font-size:18px; font-weight:400; }
        .db-alert.on .db-alert-n { color:${GREEN}; }
        .db-thumb { width:28px; height:28px; border-radius:6px; background:#ececec; color:#333; display:inline-flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
        .db-empty { font-size:13px; color:#9CA3AF; padding:18px 0; text-align:center; }

        /* línea */
        .db-line-wrap { position:relative; }
        .db-line-svg { width:100%; height:210px; display:block; cursor:crosshair; }
        .db-line-grid { stroke:#F0F0F0; stroke-width:1; }
        .db-line { fill:none; stroke-width:2.5; stroke-linecap:round; vector-effect:non-scaling-stroke; }
        .db-line.ink { stroke:${INK}; } .db-line.green { stroke:${GREEN}; stroke-width:3; }
        .db-line-cursor { stroke:${INK}; stroke-opacity:.15; stroke-width:1; vector-effect:non-scaling-stroke; }
        .db-tip { position:absolute; top:4px; transform:translateX(-50%); background:${INK}; color:#fff; border-radius:6px; padding:10px 12px; font-size:12px; line-height:1.6; pointer-events:none; white-space:nowrap; z-index:2; }
        .db-tip b { color:${GREEN}; }
        .db-tip-date { font-weight:700; margin-bottom:2px; }
        .db-xlabels { display:flex; justify-content:space-between; font-size:10.5px; color:#9CA3AF; margin-top:6px; }
        .db-legend { display:flex; gap:16px; margin-top:8px; font-size:11.5px; color:#6B7280; }
        .db-lg-dot { display:inline-block; width:10px; height:10px; border-radius:2px; margin-right:6px; vertical-align:-1px; }
        .db-lg-dot.ink { background:${INK}; } .db-lg-dot.green { background:${GREEN}; }

        /* skeleton */
        @keyframes dbPulse { 0%,100% { opacity:.5; } 50% { opacity:1; } }
        .db-skel { display:block; background:rgba(10,10,10,.08); border-radius:8px; animation:dbPulse 1.2s ease-in-out infinite; }
        .db-skel-num { width:90px; height:32px; }
        .db-skel-line { height:14px; margin:10px 0; }
        .db-skel-card { height:118px; border-radius:8px; }

        @media (max-width:1199px){
          .db-hero { grid-template-columns:1fr 1fr; }
          .db-board { grid-template-columns:repeat(2, minmax(0,1fr)); }
          .col8,.col4 { grid-column:span 12; }
        }
        @media (max-width:640px){
          .db-root { padding:20px 16px 32px; }
          .db-hero { grid-template-columns:1fr; padding:22px 18px; }
          .db-board { grid-template-columns:1fr; }
          .db-h-greet { font-size:22px; }
        }
        @media (prefers-reduced-motion: reduce){
          .db-anim,.db-skel{animation:none;}
          .db-wb-bar,.db-fill,.db-tk{transition:none;}
        }
      `}</style>

      <div className="db-inner">
        {/* HEADER */}
        <div className="db-header">
          <h1 className="db-h-greet">{greeting}{userName ? `, ${userName}` : ''}</h1>
          <p className="db-h-sub">
            {loading ? 'Cargando tu CRM…'
              : error ? 'No se pudo cargar el estado del CRM.'
              : `Hoy: ${fmtInt((k.messages_today || 0) + live.dMsgs)} mensajes · ${fmtInt((k.conversations_today || 0) + live.dConvs)} conversaciones nuevas · ${fmtInt(k.bot_sales_today || 0)} ventas del bot`}
          </p>
        </div>

        {error && (
          <div className="db-err">
            <span>Error al cargar los datos del dashboard.</span>
            <button className="db-btn" onClick={() => { setLoading(true); load(); }}>Reintentar</button>
          </div>
        )}

        {/* HERO */}
        <div className="db-hero db-anim">
          <div>
            <h3 className="db-hero-t">Mensajes recibidos</h3>
            {loading ? <span className="db-skel" style={{ height: 150 }} /> : <WeekBars cur={weekCur} prev={weekPrev} />}
            <div className="db-hero-legend">
              <span><i style={{ background: INK }} />Esta semana</span>
              <span><i style={{ background: `repeating-linear-gradient(135deg, ${INK} 0 2px, transparent 2px 5px)`, boxShadow: `inset 0 0 0 1.5px ${INK}` }} />Semana anterior</span>
            </div>
          </div>

          <div className="db-gauge-wrap">
            <Gauge pct={bot.ai_share_30d} />
            <div className="db-gauge-val">
              <b>{loading ? '—' : typeof bot.ai_share_30d === 'number' ? `${bot.ai_share_30d}%` : '—'}</b>
              <span>Respondido por el bot · 30 días</span>
            </div>
          </div>

          <div className="db-big">
            <div className="db-big-n"><BigNumber value={k.active_24h} loading={loading} reduced={reduced} /></div>
            <div className="db-big-row">
              <span className="db-big-l">Conversaciones<br />activas · 24 h</span>
              <button className="db-big-btn" aria-label="Ir a conversaciones" onClick={() => openConv()}><Arrow /></button>
            </div>
          </div>

          <div className="db-big">
            <div className="db-big-n"><BigNumber value={k.bot_revenue_today} money loading={loading} reduced={reduced} /></div>
            <div className="db-big-row">
              <span className="db-big-l">Ventas del bot hoy<br /><span className="db-big-trend">{fmtInt(k.bot_sales_today || 0)} pedidos{revTrend ? ` · ${revTrend}` : ''}</span></span>
              <button className="db-big-btn" aria-label="Ir a conversaciones" onClick={() => openConv()}><Arrow /></button>
            </div>
          </div>
        </div>

        {/* TABLERO DE CONVERSACIONES */}
        <div className="db-board">
          {BOARD_COLS.map((col, ci) => {
            const data = board[col.key] || { total: 0, items: [] };
            return (
              <div key={col.key} className="db-anim" style={{ animationDelay: `${80 + ci * 50}ms` }}>
                <div className="db-col-h">
                  <div>
                    <div className="db-col-t">{col.title}</div>
                    <div className="db-col-note">{col.note}</div>
                  </div>
                  <span className="db-col-n">{loading ? '—' : fmtInt(data.total)}</span>
                </div>
                <div className="db-col-list">
                  {loading ? [0, 1].map(i => <span key={i} className="db-skel db-skel-card" />)
                    : data.items.length === 0 ? <div className="db-col-empty">Sin conversaciones</div>
                    : data.items.map((c, i) => (
                      <button key={c.id} className={`db-tk ${col.key === 'escaladas' && i === 0 ? 'dark' : ''}`} onClick={() => openConv(c.phone)}>
                        <div className="db-tk-name">{c.name}</div>
                        <div className="db-tk-prev">{c.preview || 'Sin mensajes'}</div>
                        <div className="db-tk-foot">
                          <span className="db-chip">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="5" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="8" y1="3" x2="8" y2="7" /><line x1="16" y1="3" x2="16" y2="7" /></svg>
                            {fmtDate(c.ts)}
                          </span>
                          {c.unread > 0 && <span className="db-unread">{c.unread}</span>}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* EVOLUCIÓN + BOT VS ASESOR */}
        <div className="db-grid">
          <div className="db-card db-anim col8" style={{ animationDelay: '280ms' }}>
            <div className="db-ct">
              <h3>Evolución</h3>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="db-tabs">
                  {['Día', 'Semana', '30 días', 'Personalizado'].map(p => (
                    <button key={p} className={`db-tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p}</button>
                  ))}
                </div>
                {period === 'Personalizado' && (
                  <div className="db-range">
                    <input type="date" className="db-date" value={range.from} max={range.to} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} />
                    <span>→</span>
                    <input type="date" className="db-date" value={range.to} min={range.from} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} />
                  </div>
                )}
              </div>
            </div>
            {loading ? <div><span className="db-skel db-skel-line" /><span className="db-skel db-skel-line" /><span className="db-skel db-skel-line" /></div> : (
              <>
                <LineChart data={chartData} />
                <div className="db-foot">
                  <div><div className="db-foot-lbl">Mensajes recibidos</div><div className="db-foot-val">{fmtInt(sum('revenue'))}</div></div>
                  <div><div className="db-foot-lbl">Conversaciones nuevas</div><div className="db-foot-val">{fmtInt(sum('count'))}</div></div>
                  <div><div className="db-foot-lbl">Ventas del bot</div><div className="db-foot-val">{fmtInt(sum('sales'))} · {fmtMoney(sum('salesRev'))}</div></div>
                </div>
              </>
            )}
          </div>

          <div className="db-card db-anim col4" style={{ animationDelay: '320ms' }}>
            <div className="db-ct"><h3>Bot vs asesor</h3><span className="db-ct-note">últimos 30 días</span></div>
            {loading ? <div><span className="db-skel db-skel-line" /><span className="db-skel db-skel-line" /></div> : (
              <>
                {botRows.map(r => (
                  <div className="db-row" key={r.name}>
                    <span className="db-row-name">{r.name}</span>
                    <span className="db-track"><span className={`db-fill ${r.tone}`} style={{ width: `${((r.value || 0) / botMax) * 100}%` }} /></span>
                    <span className="db-row-val">{fmtInt(r.value)}</span>
                  </div>
                ))}
                <div className="db-foot">
                  <div><div className="db-foot-lbl">Ingresos del bot</div><div className="db-foot-val">{fmtMoney(bot.revenue_30d)}</div></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ALERTAS + TOP PRODUCTOS + CONTACTOS */}
        <div className="db-grid">
          <div className="db-card db-anim col4" style={{ animationDelay: '360ms' }}>
            <div className="db-ct"><h3>Alertas</h3><span className="db-ct-note">estado actual</span></div>
            {alerts.map(a => {
              const on = typeof a.value === 'number' && a.value > 0;
              return (
                <button key={a.key} className={`db-alert ${on ? 'on' : ''}`} onClick={() => openConv()}>
                  <span className="db-alert-lbl">{a.label}</span>
                  <span className="db-alert-n">{loading ? '—' : fmtInt(a.value)}</span>
                </button>
              );
            })}
          </div>

          <div className="db-card db-anim col4" style={{ animationDelay: '400ms' }}>
            <div className="db-ct"><h3>Top productos del bot</h3><span className="db-ct-note">últimos 30 días</span></div>
            {loading ? <span className="db-skel db-skel-line" /> : topProducts.length === 0 ? <div className="db-empty">El bot aún no ha cerrado ventas en el chat.</div> : (
              topProducts.map((p, i) => {
                const mx = Math.max(...topProducts.map(x => x.qty || 0), 1);
                return (
                  <div className="db-row" key={i}>
                    <span className="db-thumb">{(p.title || '?').trim().charAt(0).toUpperCase()}</span>
                    <span className="db-row-name" title={p.title}>{p.title}</span>
                    <span className="db-track" style={{ flexBasis: 64 }}><span className="db-fill green" style={{ width: `${(p.qty / mx) * 100}%` }} /></span>
                    <span className="db-row-val">{fmtInt(p.qty)}</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="db-card db-anim col4" style={{ animationDelay: '440ms' }}>
            <div className="db-ct"><h3>Contactos</h3><span className="db-ct-note">nuevos</span></div>
            <div className="db-row"><span className="db-row-name">Hoy</span><span className="db-row-val">{loading ? '—' : fmtInt(ct.new_today)}</span></div>
            <div className="db-row"><span className="db-row-name">Últimos 30 días</span><span className="db-row-val">{loading ? '—' : fmtInt(ct.new_30d)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
